import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  getCarryoverPreviewVisitsVisitIdCarryoverPreviewGet,
  getCarryoverSourcesVisitsVisitIdCarryoverSourcesGet,
} from '../../services/api/sdk.gen';
import type { CarryoverSourceResponse } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { useToast } from '../../hooks/useToastContext';
import { STATUS_DISPLAY_LABELS } from '../task/taskStatus';

interface CarryoverSourceMenuProps {
  visitId: string;
  /**
   * Called with the copy to apply. The caller does NOT save it.
   *
   * `copyableQids` is the full scope of the copy — wider than `answers`, which
   * holds only the source's non-empty ones. A qid in the scope but not in the
   * answers was blank on the source, and the caller clears it rather than
   * leaving this visit's older answer standing under the copy.
   */
  onAnswersLoaded: (
    answers: Record<string, unknown>,
    copyableQids: string[],
    sourceLabel: string
  ) => void;
}

const statusLabel = (status: string): string =>
  STATUS_DISPLAY_LABELS[status] ?? status.replace(/_/g, ' ');

/**
 * Dropdown of previous visits on this form to copy answers from.
 *
 * Choosing one only *loads* its answers — the caller stages them in the form
 * for review. Nothing is saved until the clinician commits.
 */
export const CarryoverSourceMenu: React.FC<CarryoverSourceMenuProps> = ({
  visitId,
  onAnswersLoaded,
}) => {
  const { error: showError } = useToast();
  const [sources, setSources] = useState<CarryoverSourceResponse[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSourceId, setLoadingSourceId] = useState<string | null>(null);

  // Fetch on first open rather than on mount: most visits never use carryover,
  // and this keeps it off the visit-load critical path.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setIsLoading(true);

    void (async () => {
      try {
        const response =
          await getCarryoverSourcesVisitsVisitIdCarryoverSourcesGet({
            path: { visit_id: visitId },
          });
        if (!cancelled) setSources(response.data ?? []);
      } catch (error) {
        console.error('Failed to load carryover sources:', error);
        if (!cancelled) showError('Unable to load previous visits.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, visitId, showError]);

  /**
   * The visit this menu is still live for, or null once it has gone away.
   *
   * A preview request can outlive the visit that started it — the clinician can
   * open another chart while it is in flight. Answers live in a store shared by
   * every visit, so applying a late result would write the previous patient's
   * answers into the chart now on screen, with no banner and no autosave hold
   * (that staging state belongs to the unmounted form). Drop late results.
   */
  const liveVisitIdRef = useRef<string | null>(visitId);
  useEffect(() => {
    liveVisitIdRef.current = visitId;
    return () => {
      liveVisitIdRef.current = null;
    };
  }, [visitId]);

  const handleSelect = useCallback(
    async (source: CarryoverSourceResponse) => {
      setLoadingSourceId(source.visit_id);
      try {
        const response =
          await getCarryoverPreviewVisitsVisitIdCarryoverPreviewGet({
            path: { visit_id: visitId },
            query: { source_visit_id: source.visit_id },
          });
        if (liveVisitIdRef.current !== visitId) return;
        onAnswersLoaded(
          response.data?.answers ?? {},
          response.data?.copyable_qids ?? [],
          formatDateToMMDDYYYY(source.visit_date)
        );
      } catch (error) {
        console.error('Failed to load carryover answers:', error);
        if (liveVisitIdRef.current === visitId) {
          showError('Unable to copy answers from that visit.');
        }
      } finally {
        if (liveVisitIdRef.current === visitId) setLoadingSourceId(null);
      }
    },
    [visitId, onAnswersLoaded, showError]
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' size='sm'>
          Copy from previous visit
          <ChevronDown className='ml-1 h-3.5 w-3.5' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='max-h-80 w-72 overflow-y-auto'
      >
        {isLoading ? (
          <div className='flex items-center justify-center py-4'>
            <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
          </div>
        ) : sources.length === 0 ? (
          <p className='px-2 py-3 text-center text-xs text-muted-foreground'>
            No previous visits to copy from.
          </p>
        ) : (
          sources.map(source => (
            <DropdownMenuItem
              key={source.visit_id}
              disabled={loadingSourceId !== null}
              onSelect={() => void handleSelect(source)}
              className='flex flex-col items-start gap-0.5'
            >
              <span className='flex w-full items-center justify-between gap-2'>
                <span className='text-xs font-medium'>
                  {source.visit_type_display_name} ·{' '}
                  {formatDateToMMDDYYYY(source.visit_date)}
                  {source.time_in ? ` · ${source.time_in}` : ''}
                </span>
                {loadingSourceId === source.visit_id && (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                )}
              </span>
              <span className='text-xs text-muted-foreground'>
                {statusLabel(source.status)} · {source.copyable_answer_count}{' '}
                answers
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
